"use client";

import { useEffect, useMemo, useState } from "react";
import { useReducedMotion } from "motion/react";

import { cn } from "@/lib/utils";

interface LensHeroRotatorProps {
  items: string[];
  intervalMs?: number;
}

export function LensHeroRotator({
  items,
  intervalMs = 2400,
}: LensHeroRotatorProps) {
  const shouldReduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);

  const safeItems = useMemo(() => (items.length ? items : ["anything."]), [items]);
  const longestItem = useMemo(
    () =>
      safeItems.reduce((longest, item) =>
        item.length > longest.length ? item : longest
      ),
    [safeItems]
  );

  useEffect(() => {
    if (shouldReduceMotion || safeItems.length <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % safeItems.length);
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [intervalMs, safeItems.length, shouldReduceMotion]);

  return (
    <span className="relative inline-grid text-left align-top italic text-foreground">
      <span className="invisible col-start-1 row-start-1">{longestItem}</span>
      {safeItems.map((item, index) => {
        const isActive = index === activeIndex;

        return (
          <span
            key={item}
            aria-hidden={!isActive}
            className={cn(
              "col-start-1 row-start-1 transition-all",
              shouldReduceMotion
                ? isActive
                  ? "opacity-100"
                  : "opacity-0"
                : isActive
                  ? "translate-y-0 opacity-100 duration-300"
                  : "translate-y-2 opacity-0 duration-300"
            )}
          >
            {item}
          </span>
        );
      })}
    </span>
  );
}
